import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  User, Mail, BookOpen, Award, Flame, Target, LogOut, Share2, Bell, Loader2, BarChart3, ClipboardList, CloudDownload, Clock, ShieldCheck, Users, FileText, Scale,
} from "lucide-react";
import {
  isPushSupported, getPushSubscription, subscribeToPush, unsubscribeFromPush, sendTestPush,
} from "@/lib/push";
import { usePeriodicSync } from "@/lib/periodicSync";
import StudyPrefsCard from "@/components/StudyPrefsCard";
import FsrsTuningCard from "@/components/FsrsTuningCard";

export default function StudentAccount() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushPermission, setPushPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const supported = isPushSupported();
  const periodic = usePeriodicSync();

  // Daily Review Reminder (FSRS-driven push at preferred study hour)
  const browserTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Manila"; }
    catch { return "Asia/Manila"; }
  })();
  const [reminder, setReminder] = useState({ enabled: false, hour: 19, timezone: browserTz, streak_save_enabled: true });
  const [reminderBusy, setReminderBusy] = useState(false);

  // Social & Privacy (Phase 3 — wired to /me/social + /me/social/privacy)
  const [social, setSocial] = useState(null); // { social_setup_complete, is_minor, social_privacy }
  const [privacyBusy, setPrivacyBusy] = useState(false);

  useEffect(() => {
    api.get("/me/social")
      .then((r) => setSocial(r.data))
      .catch(() => setSocial(null));
  }, []);

  const savePrivacy = async (patch, successMsg) => {
    if (!social) return;
    const nextPrivacy = { ...(social.social_privacy || {}), ...patch };
    setSocial({ ...social, social_privacy: nextPrivacy });
    setPrivacyBusy(true);
    try {
      const r = await api.put("/me/social/privacy", patch);
      setSocial((s) => ({ ...s, social_privacy: r.data.social_privacy }));
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save privacy setting");
      // revert
      api.get("/me/social").then((r2) => setSocial(r2.data)).catch(() => {});
    } finally {
      setPrivacyBusy(false);
    }
  };

  useEffect(() => {
    api.get("/student/reminder-settings")
      .then((r) => {
        setReminder({
          enabled: !!r.data.enabled,
          hour: Number.isInteger(r.data.hour) ? r.data.hour : 19,
          timezone: r.data.timezone || browserTz,
          streak_save_enabled: r.data.streak_save_enabled !== false,
        });
      })
      .catch(() => {});
  }, [browserTz]);

  const saveReminder = async (patch) => {
    const next = { ...reminder, ...patch };
    setReminder(next);
    setReminderBusy(true);
    try {
      await api.put("/student/reminder-settings", next);
      if (patch.enabled === true) {
        toast.success(`Daily reminder set for ${formatHour(next.hour)} (${next.timezone})`);
      } else if (patch.enabled === false) {
        toast.success("Daily reminder turned off");
      } else if (patch.streak_save_enabled === true) {
        toast.success("Streak-save reminder enabled");
      } else if (patch.streak_save_enabled === false) {
        toast.success("Streak-save reminder disabled");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save reminder");
    } finally {
      setReminderBusy(false);
    }
  };

  const testReminder = async () => {
    setReminderBusy(true);
    try {
      const r = await api.post("/student/reminder-settings/test");
      toast.success(`Test sent — ${r.data?.due_today ?? 0} card(s) due`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setReminderBusy(false);
    }
  };

  const testStreakSave = async () => {
    setReminderBusy(true);
    try {
      const r = await api.post("/student/reminder-settings/test-streak-save");
      toast.success(`Streak-save sample sent (${r.data?.sample_streak ?? 0}-day streak)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setReminderBusy(false);
    }
  };

  useEffect(() => {
    api.get("/student/dashboard").then((r) => setStats(r.data)).catch(() => {});
    if (supported) {
      getPushSubscription().then((s) => setPushOn(!!s)).catch(() => {});
    }
  }, [supported]);

  const togglePush = async (next) => {
    setPushBusy(true);
    try {
      if (next) {
        await subscribeToPush();
        setPushOn(true);
        setPushPermission(Notification.permission);
        toast.success("Notifications enabled");
      } else {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.success("Notifications disabled");
      }
    } catch (e) {
      toast.error(e?.message || "Could not update notifications");
      if (typeof Notification !== "undefined") setPushPermission(Notification.permission);
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    setPushBusy(true);
    try {
      const r = await sendTestPush();
      toast.success(r?.message || "Test sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Test failed");
    } finally { setPushBusy(false); }
  };

  const initial = (user?.full_name || "?").charAt(0).toUpperCase();

  return (
    <div className="space-y-5" data-testid="student-account-page">
      <section>
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Profile</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-1">My Account</h1>
      </section>

      <Card className="p-5 border-slate-200">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-blue-600 text-white grid place-items-center font-display font-extrabold text-2xl">
            {initial}
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-bold truncate">{user?.full_name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate">
              <Mail className="w-3 h-3 shrink-0" /> {user?.email}
            </div>
            <Badge variant="outline" className="mt-1.5 capitalize text-[10px]">{user?.status}</Badge>
          </div>
        </div>
      </Card>

      <Card className="p-4 border-slate-200">
        <h3 className="font-display font-bold text-sm mb-3">Profile Details</h3>
        <ul className="space-y-2.5 text-sm">
          <Row icon={BookOpen} label="Course" value={user?.selected_course_name || "—"} />
          <Row icon={User} label="School" value={user?.school || "—"} />
          <Row icon={Mail} label="Contact" value={user?.contact_number || "—"} />
        </ul>
      </Card>

      {stats && (
        <Card className="p-4 border-slate-200" data-testid="account-quick-stats">
          <h3 className="font-display font-bold text-sm mb-3">Quick Stats</h3>
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={Target} label="Accuracy" value={`${stats.score_percentage}%`} />
            <Stat icon={Flame} label="Streak" value={`${stats.streak?.current_streak ?? 0}d`} />
            <Stat icon={Award} label="Badges" value={`${(stats.badges_earned || []).length}/${stats.badges_total ?? 0}`} />
          </div>
        </Card>
      )}

      <Card className="p-4 border-slate-200" data-testid="account-notifications-card">
        <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2">
          <Bell className="w-4 h-4 text-violet-600" /> Notifications
        </h3>
        {!supported ? (
          <p className="text-xs text-slate-500">This browser doesn't support push notifications.</p>
        ) : pushPermission === "denied" ? (
          <p className="text-xs text-rose-600">Notifications are blocked. Enable them in your browser site settings.</p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">Push notifications</div>
                <div className="text-xs text-slate-500 mt-0.5">Get notified for badge unlocks and streak reminders.</div>
              </div>
              <Switch
                checked={pushOn}
                onCheckedChange={togglePush}
                disabled={pushBusy}
                data-testid="push-toggle"
              />
            </div>
            {pushOn && (
              <Button
                size="sm"
                variant="outline"
                className="mt-3 w-full"
                onClick={testPush}
                disabled={pushBusy}
                data-testid="push-test-btn"
              >
                {pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Bell className="w-3.5 h-3.5 mr-1.5" />}
                Send a test notification
              </Button>
            )}
          </>
        )}
      </Card>

      <Card className="p-4 border-slate-200" data-testid="account-daily-reminder-card">
        <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-600" /> Daily Review Reminder
        </h3>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-800">Remind me at my study hour</div>
            <div className="text-xs text-slate-500 mt-0.5">
              We'll send a single push notification at your chosen hour if you have FSRS cards due.
            </div>
          </div>
          <Switch
            checked={reminder.enabled}
            onCheckedChange={(v) => saveReminder({ enabled: v })}
            disabled={reminderBusy || !supported}
            data-testid="daily-reminder-toggle"
          />
        </div>

        {reminder.enabled && (
          <div className="mt-4 space-y-3" data-testid="daily-reminder-controls">
            <div className="flex items-center justify-between gap-3">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Hour</label>
              <Select
                value={String(reminder.hour)}
                onValueChange={(v) => saveReminder({ hour: parseInt(v, 10) })}
                disabled={reminderBusy}
              >
                <SelectTrigger className="w-[140px]" data-testid="daily-reminder-hour">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {Array.from({ length: 24 }, (_, h) => (
                    <SelectItem key={h} value={String(h)} data-testid={`reminder-hour-opt-${h}`}>
                      {formatHour(h)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-3 text-xs">
              <span className="font-semibold text-slate-700 uppercase tracking-wider">Timezone</span>
              <span className="text-slate-600 font-mono">{reminder.timezone}</span>
            </div>
            {!pushOn && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                Push notifications must be enabled above for daily reminders to deliver.
              </p>
            )}
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={testReminder}
              disabled={reminderBusy || !pushOn}
              data-testid="daily-reminder-test-btn"
            >
              {reminderBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Bell className="w-3.5 h-3.5 mr-1.5" />}
              Send a sample reminder now
            </Button>
          </div>
        )}

        {/* Streak-Save: separate opt-out toggle inside the same card */}
        <div className="mt-4 pt-4 border-t border-slate-200" data-testid="streak-save-section">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5 text-orange-500" /> Save my streak
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                If you're about to break a 3+ day streak, we'll nudge you at 9 PM local time so you can save it.
              </div>
            </div>
            <Switch
              checked={reminder.streak_save_enabled}
              onCheckedChange={(v) => saveReminder({ streak_save_enabled: v })}
              disabled={reminderBusy || !supported}
              data-testid="streak-save-toggle"
            />
          </div>
          {reminder.streak_save_enabled && (
            <Button
              size="sm"
              variant="outline"
              className="w-full mt-3"
              onClick={testStreakSave}
              disabled={reminderBusy || !pushOn}
              data-testid="streak-save-test-btn"
            >
              {reminderBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Flame className="w-3.5 h-3.5 mr-1.5 text-orange-500" />}
              Preview a streak-save nudge
            </Button>
          )}
        </div>
      </Card>

      {social?.social_setup_complete && (
        <Card className="p-4 border-slate-200" data-testid="account-social-privacy-card">
          <h3 className="font-display font-bold text-sm mb-1 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-indigo-600" /> Social &amp; Privacy
          </h3>
          <p className="text-[11px] text-slate-500 mb-3">
            Control how friends find you, what they see, and which push notifications
            you receive. {social.is_minor && (
              <span className="text-amber-700 font-medium">
                Some options are limited because you&apos;re under 18.
              </span>
            )}
          </p>

          <div className="space-y-4">
            {/* Reaction push notifications */}
            <div className="flex items-center justify-between gap-3" data-testid="privacy-reaction-row">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">Reaction push notifications</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Get notified when a friend reacts (🎉🔥👏) to your achievements.
                </div>
              </div>
              <Switch
                checked={(social.social_privacy?.notify_on_reactions ?? true) === true}
                onCheckedChange={(v) => savePrivacy(
                  { notify_on_reactions: v },
                  v ? "Reaction notifications enabled" : "Reaction notifications disabled",
                )}
                disabled={privacyBusy}
                data-testid="privacy-notify-reactions-toggle"
              />
            </div>

            {/* Findable by QR */}
            <div className="space-y-1.5" data-testid="privacy-findable-row">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800">Findable by QR</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Who can scan your QR code and send you a friend request.
                  </div>
                </div>
              </div>
              <Select
                value={social.social_privacy?.findable_by_qr || (social.is_minor ? "nobody" : "everyone")}
                onValueChange={(v) => savePrivacy(
                  { findable_by_qr: v },
                  "Findable preference saved",
                )}
                disabled={privacyBusy}
              >
                <SelectTrigger className="w-full sm:w-[260px]" data-testid="privacy-findable-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="everyone" data-testid="privacy-findable-everyone">Everyone</SelectItem>
                  <SelectItem value="friends_of_friends" data-testid="privacy-findable-fof">Friends of friends</SelectItem>
                  <SelectItem value="nobody" data-testid="privacy-findable-nobody">Nobody</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Share achievements */}
            <div className="flex items-center justify-between gap-3" data-testid="privacy-share-achievements-row">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">Share achievements with friends</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Post your badge unlocks, streak milestones and mock exam passes to friends&apos; feeds.
                </div>
              </div>
              <Switch
                checked={(social.social_privacy?.share_achievements || "off") === "on"}
                onCheckedChange={(v) => savePrivacy(
                  { share_achievements: v ? "on" : "off" },
                  v ? "Sharing enabled" : "Sharing disabled",
                )}
                disabled={privacyBusy}
                data-testid="privacy-share-achievements-toggle"
              />
            </div>

            {/* Show on leaderboard */}
            <div className="flex items-center justify-between gap-3" data-testid="privacy-leaderboard-row">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">Show me on the leaderboard</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Appear on course leaderboards. Your display handle is shown, never your full name.
                </div>
              </div>
              <Switch
                checked={(social.social_privacy?.show_on_leaderboard || "off") === "on"}
                onCheckedChange={(v) => savePrivacy(
                  { show_on_leaderboard: v ? "on" : "off" },
                  v ? "Visible on leaderboard" : "Hidden from leaderboard",
                )}
                disabled={privacyBusy}
                data-testid="privacy-leaderboard-toggle"
              />
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100">
            <Button
              size="sm"
              variant="ghost"
              className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 px-2"
              onClick={() => nav("/student/friends")}
              data-testid="privacy-friends-link"
            >
              <Users className="w-3.5 h-3.5 mr-1.5" /> Manage friends &amp; QR
            </Button>
          </div>
        </Card>
      )}

      <StudyPrefsCard />
      <FsrsTuningCard />

      {periodic.supported && (
        <Card className="p-4 border-slate-200" data-testid="account-periodic-sync-card">
          <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2">
            <CloudDownload className="w-4 h-4 text-emerald-600" /> Background Pre-fetch
          </h3>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800">Pre-fetch study data in background</div>
              <p className="text-xs text-slate-500 mt-0.5">Refreshes your dashboard, Smart Review summary, and analytics every ~12 hours so the app stays fresh even on poor connections.</p>
            </div>
            <Switch
              checked={periodic.enabled}
              onCheckedChange={(v) => periodic.toggle(v).then(() => {
                if (v && !periodic.lastError) toast.success("Background pre-fetch enabled");
                if (!v) toast.success("Background pre-fetch disabled");
              })}
              disabled={periodic.busy || periodic.permission === "denied"}
              data-testid="periodic-sync-toggle"
            />
          </div>
          {periodic.permission === "denied" && (
            <p className="text-[11px] text-rose-600 mt-2">Background pre-fetch is blocked. Enable it in your browser site settings.</p>
          )}
          {periodic.lastError && (
            <p className="text-[11px] text-amber-700 mt-2">{periodic.lastError}</p>
          )}
        </Card>
      )}

      <Card className="p-4 border-slate-200 space-y-2">
        <Button variant="outline" className="w-full justify-start" onClick={() => nav("/student/profile")} data-testid="account-edit-profile-btn">
          <User className="w-4 h-4 mr-2" /> Edit Profile &amp; Security
        </Button>
        <Button variant="outline" className="w-full justify-start" onClick={() => nav("/student/analytics")} data-testid="account-analytics-btn">
          <BarChart3 className="w-4 h-4 mr-2" /> My Analytics
        </Button>
        <Button variant="outline" className="w-full justify-start" onClick={() => nav("/student/enrollments")} data-testid="account-enrollments-btn">
          <ClipboardList className="w-4 h-4 mr-2" /> My Enrollments
        </Button>
        <Button variant="outline" className="w-full justify-start" onClick={() => nav("/student/progress-card")} data-testid="account-progress-card-btn">
          <Share2 className="w-4 h-4 mr-2" /> My Progress Card
        </Button>
        <Button variant="outline" className="w-full justify-start" onClick={() => nav("/student/support")} data-testid="account-support-btn">
          <Mail className="w-4 h-4 mr-2" /> Support
        </Button>

        {/* Legal — Privacy Policy + Terms of Use. Available anytime from
            the student's Account page so App Store / Play policies (and
            Philippine Data Privacy Act, RA 10173) are always one tap away. */}
        <Button variant="outline" className="w-full justify-start" onClick={() => nav("/legal/privacy")} data-testid="account-privacy-btn">
          <ShieldCheck className="w-4 h-4 mr-2" /> Privacy Policy
        </Button>
        <Button variant="outline" className="w-full justify-start" onClick={() => nav("/legal/terms")} data-testid="account-terms-btn">
          <Scale className="w-4 h-4 mr-2" /> Terms of Use
        </Button>

        <Button variant="ghost" className="w-full justify-start text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => { logout(); nav("/login"); }} data-testid="account-logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </Card>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <li className="flex items-center gap-3">
      <div className="h-7 w-7 rounded-md bg-slate-100 grid place-items-center text-slate-500">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
        <div className="text-sm text-slate-800 truncate">{value}</div>
      </div>
    </li>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="text-center">
      <div className="h-9 w-9 rounded-full mx-auto bg-blue-50 text-blue-700 grid place-items-center">
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <div className="font-display font-bold text-lg mt-1.5 leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mt-1 font-semibold">{label}</div>
    </div>
  );
}

// Formats an hour-of-day (0..23) as a 12-hour string. Matches PrepMasters'
// Filipino audience expectation ("7:00 PM" vs "19:00").
function formatHour(h) {
  const ampm = h < 12 ? "AM" : "PM";
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:00 ${ampm}`;
}
